import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { cancelQuote, listQuotes, quoteTotal } from '../services/quotesService';
import { formatDate, formatMoney } from '../lib/format';
import { friendlyError } from '../lib/errors';
import DataTable from '../components/ui/DataTable';
import SearchInput from '../components/ui/SearchInput';
import Pagination from '../components/ui/Pagination';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';
import ConfirmDialog from '../components/ui/ConfirmDialog';

export default function QuotesPage() {
  const { ruc, user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const canManage = user?.rol !== 'prueba';

  const fetchPage = useCallback(
    async ({ reset = false } = {}) => {
      if (!ruc) return;
      if (reset) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        const result = await listQuotes({
          ruc,
          search,
          includeCancelled,
          cursor: reset ? null : cursor,
        });
        setItems((prev) => (reset ? result.items : [...prev, ...result.items]));
        setCursor(result.cursor || null);
        setHasMore(!!result.hasMore);
      } catch (e) {
        setError(friendlyError(e));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [ruc, search, includeCancelled, cursor]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setItems([]);
      setCursor(null);
      fetchPage({ reset: true });
    }, search ? 280 : 0);
    return () => clearTimeout(t);
  }, [ruc, search, includeCancelled]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCancel = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      await cancelQuote(confirm.id);
      setConfirm(null);
      await fetchPage({ reset: true });
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { key: 'cotizacion', label: 'N°', render: (r) => r.cotizacion ?? '—' },
    { key: 'cliente', label: 'Cliente', render: (r) => r.cliente || '—' },
    { key: 'fecha', label: 'Fecha', render: (r) => formatDate(r.fecha) },
    { key: 'items', label: 'Ítems', render: (r) => (r.items?.length ?? 0) },
    { key: 'total', label: 'Total', render: (r) => formatMoney(quoteTotal(r)) },
    {
      key: 'estado',
      label: 'Estado',
      render: (r) =>
        r.cancelado ? <Badge tone="muted">Cancelada</Badge> : <Badge tone="ok">Vigente</Badge>,
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (r) => (
        <div className="row-actions">
          <button
            type="button"
            className="btn btn-mini"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/cotizaciones/${r.id}`);
            }}
          >
            Ver
          </button>
          {canManage && !r.cancelado ? (
            <button
              type="button"
              className="btn btn-mini btn-ghost"
              onClick={(e) => {
                e.stopPropagation();
                setConfirm({ id: r.id, num: r.cotizacion });
              }}
            >
              Cancelar
            </button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Cotizaciones</h1>
          <p className="muted">
            Colección <code>cotizaciones</code> · la conversión a pedido/stock se hace en la app móvil
          </p>
        </div>
      </header>

      <div className="toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar N° o cliente…" />
        <label className="checkbox">
          <input
            type="checkbox"
            checked={includeCancelled}
            onChange={(e) => setIncludeCancelled(e.target.checked)}
          />
          Incluir canceladas
        </label>
      </div>

      {loading ? (
        <LoadingState label="Cargando cotizaciones…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => fetchPage({ reset: true })} />
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={items}
            keyField="id"
            onRowClick={(row) => navigate(`/admin/cotizaciones/${row.id}`)}
            empty={<EmptyState title="No hay cotizaciones" description="Se crean desde la app móvil." />}
          />
          <Pagination hasMore={hasMore} onMore={() => fetchPage()} loading={loadingMore} shown={items.length} />
        </>
      )}

      <ConfirmDialog
        open={!!confirm}
        title="Cancelar cotización"
        message={`¿Marcar la cotización N° ${confirm?.num} como cancelada? No se modifica stock.`}
        confirmLabel="Cancelar cotización"
        onConfirm={onCancel}
        onCancel={() => setConfirm(null)}
        loading={busy}
      />
    </div>
  );
}
